import ApiService, { ApiResponse } from './ApiService';
import { PAGINATION_CONSTANTS } from '../constants/pagination';

export interface User {
  id?: string | number;
  user_id?: string;
  name?: string;
  first_name?: string | null;
  last_name?: string | null;
  middle_name?: string | null;
  username: string;
  email: string;
  role: 'user' | 'admin' | 'manager';
  status?: 'active' | 'inactive';
  is_active?: boolean;
  is_deleted?: boolean;
  specialization?: string | null;
  phone?: string | null;
  affiliation?: string;
  country_code?: string;
  created_at?: string;
  last_login?: string | null;
  updated_at?: string;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface UserListResponse {
  users: User[];
  total_count: number; // Used internally
  total?: number; // API response uses 'total' key
  page: number;
  size: number;
  total_pages: number;
}

export interface UserListParams {
  user_id?: string;
  username?: string;
  page?: number;
  size?: number;
  is_active?: boolean | string;
  role?: string;
}

export interface CreateUserRequest {
  id?: string; // Optional id for update operations (becomes user_id in API)
  user_id?: number; // For edit operations
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  username: string;
  password: string;
  email: string;
  role: 'user' | 'admin' | 'manager';
  status?: 'active' | 'inactive';
  is_active?: boolean;
  specialization?: string | null;
  phone?: string | null;
  affiliation?: string;
}

class UserService {
  private static instance: UserService;
  private apiService: ApiService;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  public async getUsers(params?: UserListParams): Promise<ApiResponse<UserListResponse>> {
    try {
      // Build query parameters for the API
      // Only include search when a non-empty value is provided
      const queryParams: Record<string, any> = {
        user_id: params?.user_id || 'all',
        page: params?.page || 1,
        size: params?.size || PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
        is_active: params?.is_active !== undefined ? params.is_active : 'all',
        role: params?.role || 'user',
      };
      const searchValue = params?.username?.toString().trim();
      if (searchValue) {
        queryParams.search = searchValue;
      }

      // Call the API using AUTH_SERVICE_BASEURL (same as login and role APIs)
      const baseUrl = this.apiService.getAuthServiceUrl();

      const response = await this.apiService.get<any>(
        'users',
        queryParams,
        baseUrl
      );

      if (!response.success) {
        console.error('Failed to fetch users:', response.error);
        return {
          success: false,
          error: response.error || 'Failed to fetch users'
        };
      }

      // Parse the API response
      const data = response.data;

      // Handle different possible response structures
      let userListResponse: UserListResponse;

      if (data.is_success && data.data && Array.isArray(data.data)) {
        // API returns: { is_success, data: [{ users, total, ... }] }
        const userData = data.data[0] || {};
        const totalCount = userData.total_count ?? userData.total ?? (userData.users?.length || 0);
        const pageSize = userData.page_size ?? userData.size ?? queryParams.size;
        const pageNumber = userData.page ?? userData.page_number ?? queryParams.page;
        const totalPages = userData.total_pages ?? Math.max(1, Math.ceil(totalCount / pageSize || 1));

        userListResponse = {
          users: userData.users || [],
          total_count: totalCount,
          total: userData.total ?? userData.total_count,
          page: pageNumber,
          size: pageSize,
          total_pages: totalPages
        };
      } else if (data.users && Array.isArray(data.users)) {
        // Direct response structure: { users, total, ... }
        const totalCount = data.total_count ?? data.total ?? data.users.length;
        const pageSize = data.page_size ?? data.size ?? queryParams.size;
        const pageNumber = data.page ?? data.page_number ?? queryParams.page;
        const totalPages = data.total_pages ?? Math.max(1, Math.ceil(totalCount / pageSize || 1));

        userListResponse = {
          users: data.users,
          total_count: totalCount,
          total: data.total ?? data.total_count,
          page: pageNumber,
          size: pageSize,
          total_pages: totalPages
        };
      } else {
        console.error('Unexpected API response structure:', data);
        return {
          success: false,
          error: 'Invalid response structure from API'
        };
      }

      return {
        success: true,
        data: userListResponse
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred while fetching users'
      };
    }
  }

  public async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    try {
      // Use AUTH_SERVICE_BASEURL for consistency with other user APIs
      const baseUrl = this.apiService.getAuthServiceUrl();

      // Prepare the request payload according to API specification
      const payload: any = {
        first_name: userData.first_name || null,
        middle_name: userData.middle_name || null,
        last_name: userData.last_name || null,
        username: userData.username,
        email: userData.email,
        role: userData.role,
        phone: userData.phone || null,
        specialization: userData.specialization || null,
        is_active: userData.is_active !== undefined ? userData.is_active : true,
      };

      // Only include password if provided (for edit operations when user wants to change password)
      // For new users, password is auto-generated by backend and sent via email
      if (userData.password && userData.password.trim()) {
        payload.password = userData.password;
      }

      // For edit operations, include user_id
      if (userData.id || userData.user_id) {
        payload.user_id = userData.user_id || parseInt(userData.id || '0', 10);
      }

      // Call POST /users endpoint
      const response = await this.apiService.post<any>(
        'users',
        payload,
        baseUrl
      );

      if (!response.success) {
        console.error('Failed to save user:', response.error);
        return {
          success: false,
          error: response.error || 'Failed to save user'
        };
      }

      // Check if API returned is_success: false
      if (response.data?.is_success === false) {
        return {
          success: false,
          error: response.data?.message || 'Failed to save user'
        };
      }

      // Handle different response structures
      let savedUser: User;

      if (response.data?.is_success && response.data?.data) {
        // API returns: { is_success, data: {...} }
        savedUser = response.data.data;
      } else if (response.data?.user) {
        // API returns: { user: {...} }
        savedUser = response.data.user;
      } else {
        // Direct user object
        savedUser = response.data;
      }

      return {
        success: true,
        data: savedUser
      };
    } catch (error) {
      console.error('Error saving user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred while saving user'
      };
    }
  }

  public async updateUser(userId: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    const baseUrl = this.apiService.getAuthServiceUrl();
    return this.apiService.put<User>(`users/${userId}`, userData, baseUrl);
  }

  public async deleteUser(userId: string | number): Promise<ApiResponse<void>> {
    try {
      const baseUrl = this.apiService.getAuthServiceUrl();

      // Use path format: /users/user_id=3
      const response = await this.apiService.delete<any>(
        `users/user_id=${userId}`,
        undefined,
        baseUrl
      );

      if (!response.success) {
        console.error('Failed to delete user:', response.error);
        return {
          success: false,
          error: response.error || 'Failed to delete user'
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      console.error('Error deleting user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred while deleting user'
      };
    }
  }

  public async getUserById(userId: string): Promise<ApiResponse<User>> {
    const baseUrl = this.apiService.getAuthServiceUrl();
    return this.apiService.get<User>(`users/${userId}`, undefined, baseUrl);
  }

  public async forcedResetUser(userId: string | number): Promise<ApiResponse<void>> {
    try {
      const baseUrl = this.apiService.getAuthServiceUrl();

      // This API sends a password reset link with username to the user's email
      // Endpoint: POST /api/v1/users/reset-credentials?user_id={userId}
      const response = await this.apiService.post<{
        is_success: boolean;
        status: number;
        message: string;
        data: string;
      }>(
        `users/reset-credentials?user_id=${userId}`,
        {},
        baseUrl
      );

      if (!response.success) {
        console.error('Failed to send password reset link:', response.error);
        return {
          success: false,
          error: response.error || 'Failed to send password reset link'
        };
      }

      // Check if API returned is_success: false
      if (response.data && response.data.is_success === false) {
        return {
          success: false,
          error: response.data.message || 'Failed to send password reset link'
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      console.error('Error sending password reset link:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred while sending password reset link'
      };
    }
  }
}

export default UserService;
